import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePhoneNumbers } from './hooks';
import { PhoneNumbersTable, SearchAndFilters, BuyNumberModal } from './components';
import { PageLoader, Button, FullWidthContainer, Modal } from '../../components';
import { useToast } from '../../contexts/useToast';
import { PurchaseNumberRequest, PhoneNumber } from './types';
import { useMinimumLoader } from '../../hooks/useMinimumLoader';

const PhoneNumbers: React.FC = () => {
  const { t } = useTranslation();
  const {
    phoneNumbers,
    loading,
    error,
    purchasePhoneNumber,
    deletePhoneNumber,
    fetchPhoneNumbers,
  } = usePhoneNumbers();
  const { showToast } = useToast();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isBuyModalOpen, setIsBuyModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    phoneNumber: PhoneNumber | null;
  }>({
    isOpen: false,
    phoneNumber: null,
  });

  const showLoader = useMinimumLoader(loading, { minimumDuration: 500 });

  const handleBuyNumber = () => {
    setIsBuyModalOpen(true);
  };

  const handlePurchaseNumber = async (data: PurchaseNumberRequest) => {
    const result = await purchasePhoneNumber(data);

    if (result.success && result.phoneNumber) {
      showToast({
        type: 'success',
        title: 'Phone Number Purchased',
        message: `Successfully purchased phone number: ${result.phoneNumber.number}`,
      });
      setIsBuyModalOpen(false);
      fetchPhoneNumbers();
    } else {
      showToast({
        type: 'error',
        title: 'Purchase Failed',
        message: result.error || 'Failed to purchase phone number',
      });
    }
  };

  const handleDeletePhoneNumber = (id: string) => {
    const phoneNumber = phoneNumbers.find(phone => phone.id === id);
    if (phoneNumber) {
      setDeleteModal({
        isOpen: true,
        phoneNumber,
      });
    }
  };

  const confirmDelete = async () => {
    if (deleteModal.phoneNumber) {
      const success = await deletePhoneNumber(deleteModal.phoneNumber.id);
      if (success) {
        showToast({
          type: 'success',
          title: 'Phone Number Deleted',
          message: `Successfully deleted ${deleteModal.phoneNumber.number}`,
        });
        setDeleteModal({ isOpen: false, phoneNumber: null });
      } else {
        showToast({
          type: 'error',
          title: 'Delete Failed',
          message: 'Failed to delete phone number. Please try again.',
        });
      }
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, phoneNumber: null });
  };

  if (showLoader) {
    return <PageLoader show={true} />;
  }

  if (error) {
    return (
      <FullWidthContainer>
        <div className="space-y-6">
          <div className="text-center py-8">
            <p className="text-red-500 text-lg font-semibold">Error loading phone numbers</p>
            <p className="text-sm text-gray-500 mt-2">{error}</p>
          </div>
        </div>
      </FullWidthContainer>
    );
  }

  return (
    <FullWidthContainer>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('phoneNumbers.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">{t('phoneNumbers.subtitle')}</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="gradient-multi"
              onClick={handleBuyNumber}
              className="flex items-center gap-2"
            >
              <span className="material-icons">add</span>
              {t('phoneNumbers.buyNumber')}
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <SearchAndFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />

        {/* Phone Numbers Table */}
        <PhoneNumbersTable
          phoneNumbers={phoneNumbers}
          onDelete={handleDeletePhoneNumber}
          onBuyNumber={handleBuyNumber}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
        />

        {/* Buy Number Modal */}
        <BuyNumberModal
          isOpen={isBuyModalOpen}
          onClose={() => setIsBuyModalOpen(false)}
          onPurchase={handlePurchaseNumber}
        />

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={deleteModal.isOpen}
          onClose={cancelDelete}
          title=""
          size="sm"
          showCloseButton={false}
        >
          <div className="text-center space-y-6">
            {/* Warning Icon */}
            <div className="mx-auto w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <span className="material-icons text-3xl text-red-600 dark:text-red-400">
                delete_forever
              </span>
            </div>
            {/* Content */}
            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('phoneNumbers.deletePhoneNumber')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('phoneNumbers.areYouSureDeletePhoneNumber')}{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  &ldquo;{deleteModal.phoneNumber?.number}&rdquo;
                </span>
                ?
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                {t('phoneNumbers.thisActionCannotBeUndonePhoneNumber')}
              </p>
            </div>
            {/* Phone Number Info Card */}
            {deleteModal.phoneNumber && (
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                    <span className="material-icons text-primary-600 dark:text-primary-400 text-sm">
                      phone
                    </span>
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {deleteModal.phoneNumber.number}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {deleteModal.phoneNumber.status_display}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="secondary"
                onClick={cancelDelete}
                className="flex-1 px-6 py-3 font-medium"
              >
                {t('phoneNumbers.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={confirmDelete}
                className="flex-1 px-6 py-3 font-medium"
              >
                <span className="material-icons text-sm mr-2">delete</span>
                {t('phoneNumbers.deleteNumber')}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </FullWidthContainer>
  );
};

export { PhoneNumbers };
